import { createRef } from "react"
import { createRoot, type Root } from "react-dom/client"
import { Extension } from "@tiptap/core"
import type { Editor, Range } from "@tiptap/core"
import { PluginKey } from "@tiptap/pm/state"
import Suggestion, { findSuggestionMatch, type SuggestionOptions } from "@tiptap/suggestion"
import { SKILL_SLASH_TOKEN_RE } from "@/components/agent/skill-slash-token"
import { SkillSlashList, type SkillSlashListHandle } from "@/components/agent/skill-slash-list"
import type { AgentSessionSkill } from "@/lib/agent-api"

const skillSlashSuggestionKey = new PluginKey("agentSkillSlashSuggestion")

/** Module flag: Enter in composer must not submit while slash menu is open. */
let skillSlashSuggestionActive = false

export function isSkillSlashSuggestionActive(): boolean {
  return skillSlashSuggestionActive
}

/** Plain text outside the active `/query` range (so in-progress token is not "used"). */
function textOutsideSuggestionRange(editor: Editor, range: Range): string {
  const doc = editor.state.doc
  const before = doc.textBetween(0, range.from, "\n", "\0")
  const after = doc.textBetween(range.to, doc.content.size, "\n", "\0")
  return `${before}${after}`
}

function usedSkillSlugs(text: string): Set<string> {
  const used = new Set<string>()
  SKILL_SLASH_TOKEN_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = SKILL_SLASH_TOKEN_RE.exec(text)) != null) {
    const slug = match[1]
    if (slug) used.add(slug.toLowerCase())
  }
  return used
}

function suggestionRangeAtCursor(editor: Editor): Range {
  const match = findSuggestionMatch({
    char: "/",
    allowSpaces: false,
    allowToIncludeChar: false,
    allowedPrefixes: [" "],
    startOfLine: false,
    $position: editor.state.selection.$from,
  })
  if (match) return match.range
  const pos = editor.state.selection.from
  return { from: pos, to: pos }
}

function unusedSkills(skills: AgentSessionSkill[], editor: Editor, range: Range): AgentSessionSkill[] {
  const used = usedSkillSlugs(textOutsideSuggestionRange(editor, range))
  return skills.filter((s) => !used.has(s.slug.toLowerCase()))
}

function filterSkills(skills: AgentSessionSkill[], query: string, editor: Editor): AgentSessionSkill[] {
  const available = unusedSkills(skills, editor, suggestionRangeAtCursor(editor))
  const q = query.trim().toLowerCase()
  const matched = q
    ? available.filter(
        (s) =>
          s.slug.toLowerCase().includes(q) ||
          s.name.toLowerCase().includes(q) ||
          (s.description ?? "").toLowerCase().includes(q),
      )
    : available
  return matched.slice(0, 40)
}

type RenderProps = {
  editor: Editor
  range: Range
  query: string
  command: (item: AgentSessionSkill) => void
  clientRect?: (() => DOMRect | null) | null
  items: AgentSessionSkill[]
}

const MENU_MAX_HEIGHT = 256
const MENU_GAP = 6

export function createSkillSlashSuggestion(
  getSkills: () => AgentSessionSkill[],
  getOnSkillPicked: () => ((skill: AgentSessionSkill) => void) | undefined = () => undefined,
): Omit<SuggestionOptions<AgentSessionSkill>, "editor"> {
  return {
    pluginKey: skillSlashSuggestionKey,
    char: "/",
    allowSpaces: false,
    allowedPrefixes: [" "],
    shouldShow: ({ editor, range }) => unusedSkills(getSkills(), editor, range).length > 0,
    items: ({ query, editor }) => filterSkills(getSkills(), query, editor),
    command: ({ editor, range, props }) => {
      const skill = props as AgentSessionSkill
      editor.chain().focus().insertContentAt(range, `/${skill.slug} `).run()
      getOnSkillPicked()?.(skill)
    },
    render: () => {
      let root: Root | null = null
      let container: HTMLDivElement | null = null
      let hostEl: HTMLElement | null = null
      const listRef = createRef<SkillSlashListHandle>()
      let editorRef: Editor | null = null
      let currentRange: Range | null = null
      let lastClientRect: (() => DOMRect | null) | null | undefined = null
      let captureEscapeHandler: ((e: KeyboardEvent) => void) | null = null

      const resolveCaretRect = (clientRect?: (() => DOMRect | null) | null): DOMRect | null => {
        const fromSuggestion = clientRect?.() ?? null
        if (fromSuggestion && (fromSuggestion.width > 0 || fromSuggestion.height > 0 || fromSuggestion.top > 0)) {
          return fromSuggestion
        }
        if (!editorRef || !currentRange) return fromSuggestion
        try {
          const start = editorRef.view.coordsAtPos(currentRange.from)
          const end = editorRef.view.coordsAtPos(Math.max(currentRange.from, currentRange.to))
          return new DOMRect(
            start.left,
            start.top,
            Math.max(1, end.right - start.left),
            Math.max(1, end.bottom - start.top),
          )
        } catch {
          return fromSuggestion
        }
      }

      const position = (clientRect?: (() => DOMRect | null) | null) => {
        if (!container) return
        lastClientRect = clientRect
        const rect = resolveCaretRect(clientRect)
        if (!rect) return

        // Transformed dialog/panel is the fixed containing block — convert viewport → host-local.
        const frame = hostEl && hostEl !== document.body ? hostEl.getBoundingClientRect() : null
        const menuHeight = Math.min(container.offsetHeight || MENU_MAX_HEIGHT, MENU_MAX_HEIGHT)
        const spaceBelow = (frame ? frame.bottom : window.innerHeight) - rect.bottom - MENU_GAP
        const spaceAbove = rect.top - (frame ? frame.top : 0) - MENU_GAP
        const openAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow

        const topVp = openAbove
          ? Math.max((frame ? frame.top : 0) + MENU_GAP, rect.top - menuHeight - MENU_GAP)
          : rect.bottom + MENU_GAP
        const menuWidth = container.offsetWidth || 288
        const minLeft = (frame ? frame.left : 0) + MENU_GAP
        const maxLeft = (frame ? frame.right : window.innerWidth) - menuWidth - MENU_GAP
        const leftVp = Math.min(Math.max(minLeft, rect.left), Math.max(minLeft, maxLeft))

        container.style.top = `${frame ? topVp - frame.top : topVp}px`
        container.style.left = `${frame ? leftVp - frame.left : leftVp}px`
      }

      const teardownPopup = () => {
        root?.unmount()
        root = null
        container?.remove()
        container = null
      }

      const ensurePopup = (props: RenderProps) => {
        // No unused matches → do not show an empty shell.
        if (props.items.length === 0) {
          teardownPopup()
          return
        }
        if (!container) {
          container = document.createElement("div")
          container.className = "pe-mention-popup"
          container.setAttribute("data-agent-portal-menu", "")
          container.style.position = "fixed"
          container.style.zIndex = "300"
          hostEl = (props.editor.view.dom.closest('[role="dialog"]') as HTMLElement | null) ?? document.body
          hostEl.appendChild(container)
          root = createRoot(container)
        }
        root?.render(<SkillSlashList ref={listRef} items={props.items} query={props.query} command={props.command} />)
        position(props.clientRect)
        requestAnimationFrame(() => position(lastClientRect))
      }

      return {
        onStart: (props: RenderProps) => {
          skillSlashSuggestionActive = true
          editorRef = props.editor
          currentRange = props.range

          captureEscapeHandler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return
            if (!currentRange || !editorRef) return
            e.stopImmediatePropagation()
            editorRef.commands.deleteRange(currentRange)
          }
          window.addEventListener("keydown", captureEscapeHandler, { capture: true })

          // Same host rule as @-mentions: dialog (if any) else body.
          // Do NOT mount into data-agent-chat-panel — ancestors often create a fixed
          // containing block; viewport top/left then land in the wrong place.
          ensurePopup(props)
        },
        onUpdate: (props: RenderProps) => {
          currentRange = props.range
          ensurePopup(props)
        },
        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (!container) return false
          return listRef.current?.onKeyDown(props.event) ?? false
        },
        onExit: () => {
          skillSlashSuggestionActive = false
          if (captureEscapeHandler) {
            window.removeEventListener("keydown", captureEscapeHandler, { capture: true })
            captureEscapeHandler = null
          }
          editorRef = null
          currentRange = null
          lastClientRect = null
          hostEl = null
          teardownPopup()
        },
      }
    },
  }
}

type SkillSlashAutocompleteOptions = {
  getSkills: () => AgentSessionSkill[]
  getOnSkillPicked: () => ((skill: AgentSessionSkill) => void) | undefined
}

/** TipTap Suggestion for `/slug` without mention nodes. */
export const SkillSlashAutocomplete = Extension.create<SkillSlashAutocompleteOptions>({
  name: "agentSkillSlashAutocomplete",

  addOptions() {
    return {
      getSkills: () => [],
      getOnSkillPicked: () => undefined,
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...createSkillSlashSuggestion(
          () => this.options.getSkills(),
          () => this.options.getOnSkillPicked(),
        ),
      }),
    ]
  },
})
